import React, { useEffect, useState, Fragment, useCallback } from "react";
import { useIsPlayerOnly } from "../hooks/useIsPlayerOnly";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import Logo from "../assets/img/LogoSF.png";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import ThemeToggle from "../components/ThemeToggle";
import { createPortal } from "react-dom";
import i18n, { LANG_USER_SET_KEY } from "../i18n";
import { Globe } from "lucide-react";
import { useIsAppAdmin } from "../hooks/useIsAppAdmin";

function setLanguage(lng: "fr" | "en") {
  try {
    window.localStorage.setItem(LANG_USER_SET_KEY, "1");
  } catch {
    // ignore (mode privé, storage désactivé, etc.)
  }
  i18n.changeLanguage(lng);
}

// Droits pour le formulaire Articles EFFR
const effrEditorEmails = [
  "cyprien.conseillon@icloud.com",
  "serge.pojato@effr-basket.fr",
];

/* ===== i18n mini-hook (même pattern que CreateMatch) ===== */
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );

  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);

  const t = useCallback(
    (key: string, options?: Record<string, unknown>): string =>
      options != null
        ? String((i18n.t as any)(key, { ...options, lng }))
        : String((i18n.t as any)(key, { lng })),
    [lng]
  );

  return { t, lng };
}

// --- Inline toggle langue (responsive mobile) ---
const InlineLanguageToggle: React.FC = () => {
  const { lng } = useI18nSafe();
  const isFR = lng.startsWith("fr");
  const next = isFR ? "en" : "fr";
  const aria = isFR ? "Passer en anglais" : "Switch to French";

  return (
    <button
      type="button"
      onClick={() => setLanguage(next as "fr" | "en")}
      aria-label={aria}
      title={aria}
      className={`
        relative inline-flex items-center justify-center shrink-0
        h-10 w-10 rounded-xl border transition
        border-gray-200 hover:bg-gray-50
        dark:border-gray-800 dark:hover:bg-gray-800
        sm:h-auto sm:w-auto sm:px-3 sm:py-2 sm:gap-2
      `}
    >
      <Globe className="w-5 h-5 sm:w-4 sm:h-4" />
      <span className="hidden sm:inline text-sm font-medium">
        {isFR ? "FR" : "EN"}
      </span>
      <span
        className="
          sm:hidden absolute -bottom-1 -right-1 text-[10px] font-semibold
          bg-gray-900 text-white dark:bg-white dark:text-gray-900
          rounded px-1 leading-4
        "
      >
        {isFR ? "FR" : "EN"}
      </span>
    </button>
  );
};

// Petit helper pour porter le Menu dans <body>
const MenuPortal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  if (!mounted) return null;
  return createPortal(children, document.body);
};

const Navbar = () => {
  const { t } = useI18nSafe();
  const { isAdmin } = useIsAppAdmin();
  const [isConnected, setIsConnected] = useState(false);
  const [isEffrEditor, setIsEffrEditor] = useState(false); // accès formulaire EFFR
  const [myClubs, setMyClubs] = useState<{ id: string; name: string }[]>([]);
  const navigate = useNavigate();
  const { isPlayerOnly } = useIsPlayerOnly();

  useEffect(() => {
    const computeRoles = (email: string | undefined | null) => {
      const safe = email ?? "";
      setIsEffrEditor(effrEditorEmails.includes(safe));
    };

    const fetchMyClubs = async (userId: string | undefined) => {
      if (!userId) {
        setMyClubs([]);
        return;
      }
      const { data: memberships } = await supabase
        .from("club_members")
        .select("club_id")
        .eq("user_id", userId)
        .eq("status", "active")
        .order("created_at", { ascending: true });

      const clubIds = Array.from(
        new Set((memberships ?? []).map((row) => row.club_id))
      );
      if (clubIds.length === 0) {
        setMyClubs([]);
        return;
      }

      const { data: clubs } = await supabase
        .from("clubs")
        .select("id, name")
        .in("id", clubIds);

      const byId = new Map((clubs ?? []).map((c) => [c.id, c.name]));
      setMyClubs(
        clubIds
          .filter((id) => byId.has(id))
          .map((id) => ({ id, name: byId.get(id) as string }))
      );
    };

    const checkUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      setIsConnected(!!user);
      computeRoles(user?.email);
      fetchMyClubs(user?.id);
    };

    checkUser();

    const { data: listener } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        const user = session?.user;
        setIsConnected(!!user);
        computeRoles(user?.email);
        fetchMyClubs(user?.id);
      }
    );

    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error("Erreur lors de la déconnexion :", error.message);
    } else {
      navigate("/");
    }
  };

  // Liste des liens i18n
  const navLinks = [
    { to: "/dashboard", k: "common:nav.dashboard", def: "Dashboard" },
    { to: "/myteam", k: "common:nav.myTeams", def: "My teams" },
    { to: "/allmatchs", k: "common:nav.myMatches", def: "My matches" },
    { to: "/trainings", k: "common:nav.trainings", def: "Trainings" },
    { to: "/matchcalendar", k: "common:nav.calendar", def: "Calendar" },
    ...(!isPlayerOnly ? [{ to: "/playcreator", k: "common:nav.playCreator", def: "PlayCreator" }] : []),
    ...(!isPlayerOnly ? [{ to: "/rotations", k: "common:nav.rotations", def: "Rotations" }] : []),
    { to: "/tournaments", k: "common:nav.tournaments", def: "Tournament" },
    { to: "/allplays", k: "common:nav.plays", def: "Plays" },
    { to: "/profile", k: "common:nav.profile", def: "Profile" },
    { to: "/news", k: "common:nav.news", def: "News" },
    { to: "/podcast", k: "common:nav.podcast", def: "Podcast" },
  ];

  return (
    <header className="sticky top-0 z-50 safe-top">
      <div
        className="
          bg-white/95 dark:bg-gray-900/95 backdrop-blur
          border border-gray-200 dark:border-gray-800
          rounded-2xl
          mx-2 sm:mx-3 mt-1
        "
      >
        <nav className="mx-auto max-w-6xl px-4 sm:px-6 py-3 flex items-center justify-between">
          {/* Gauche : logo + nom */}
          <div className="flex items-center min-w-0">
            <Link to="/dashboard" className="flex items-center min-w-0">
              <img
                src={Logo}
                alt="SwishData Logo"
                className="w-10 h-10 mr-3 shrink-0"
              />
              <span className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
                SwishData{" "}
                {isAdmin && (
                  <span className="bg-orange-500 text-white text-xs px-2 py-1 rounded ml-2 align-middle">
                    {t("common:nav.admin", { defaultValue: "Admin" })}
                  </span>
                )}
              </span>
            </Link>
          </div>

          {/* Droite : langue (visiteurs non connectés seulement, sinon dans le menu) + menu/liens */}
          <div className="flex items-center gap-3 shrink-0">
            {!isConnected && <InlineLanguageToggle />}

            {isConnected ? (
              <Menu as="div" className="relative inline-block text-left">
                {({ open }) => (
                  <>
                    <div>
                      <Menu.Button
                        className="inline-flex w-full justify-center gap-x-1.5 rounded-md
                        bg-orange-500 px-3 py-2 text-sm font-semibold text-white shadow-sm
                        hover:bg-orange-600 transition"
                      >
                        {t("common:nav.menu", { defaultValue: "Menu" })}
                        <ChevronDownIcon
                          className="-mr-1 h-5 w-5 text-white"
                          aria-hidden="true"
                        />
                      </Menu.Button>
                    </div>

                    {open && (
                      <MenuPortal>
                        <Transition
                          appear
                          show={open}
                          as={Fragment}
                          enter="transition-opacity ease-out duration-150"
                          enterFrom="opacity-0"
                          enterTo="opacity-100"
                          leave="transition-opacity ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <div className="fixed inset-0 z-[9998] bg-black/35" />
                        </Transition>

                        <Transition
                          appear
                          show={open}
                          as={Fragment}
                          enter="transition ease-out duration-150"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-100"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items
                            static
                            className="fixed z-[10000] right-2 sm:right-4
                            top-[calc(env(safe-area-inset-top)+72px)]
                            w-[min(92vw,20rem)] max-h-[80vh] overflow-y-auto
                            rounded-xl bg-white shadow-xl ring-1 ring-black/5 focus:outline-none
                            dark:bg-gray-900 dark:ring-white/10"
                          >
                            {/* Apparence */}
                            <div className="px-3 pt-3">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                                {t("common:nav.theme", { defaultValue: "Theme" })}
                              </div>
                              <ThemeToggle />
                            </div>

                            {/* Langue */}
                            <div className="px-3 pt-3">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                                {t("common:nav.language", {
                                  defaultValue: "Language",
                                })}
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setLanguage("fr")}
                                  className={`px-2 py-1 rounded-md text-sm border transition
                                  ${(i18n.resolvedLanguage || i18n.language || "")
                                    .toLowerCase()
                                    .startsWith("fr")
                                      ? "bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                                      : "border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                                    }`}
                                >
                                  FR
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setLanguage("en")}
                                  className={`px-2 py-1 rounded-md text-sm border transition
                                  ${(i18n.resolvedLanguage || i18n.language || "")
                                    .toLowerCase()
                                    .startsWith("en")
                                      ? "bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-700"
                                      : "border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                                    }`}
                                >
                                  EN
                                </button>
                              </div>
                            </div>

                            <div className="my-2 border-t border-gray-200 dark:border-gray-800" />

                            {/* Liens */}
                            <div className="py-1 text-gray-900 dark:text-gray-100">
                              {myClubs.length === 1 && (
                                <Menu.Item>
                                  {({ active }) => (
                                    <Link
                                      to={`/club/${myClubs[0].id}/dashboard`}
                                      className={`block px-3 py-2 text-[15px] font-semibold text-orange-600 dark:text-orange-400 transition ${
                                        active
                                          ? "bg-gray-100 dark:bg-gray-800"
                                          : ""
                                      }`}
                                    >
                                      {t("common:nav.myClub", {
                                        defaultValue: "My Club",
                                      })}
                                    </Link>
                                  )}
                                </Menu.Item>
                              )}

                              {myClubs.length > 1 && (
                                <>
                                  <div className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    {t("common:nav.myClubs", {
                                      defaultValue: "My Clubs",
                                    })}
                                  </div>
                                  {myClubs.map((c) => (
                                    <Menu.Item key={c.id}>
                                      {({ active }) => (
                                        <Link
                                          to={`/club/${c.id}/dashboard`}
                                          className={`block px-3 py-2 text-[15px] font-semibold text-orange-600 dark:text-orange-400 transition ${
                                            active
                                              ? "bg-gray-100 dark:bg-gray-800"
                                              : ""
                                          }`}
                                        >
                                          {c.name}
                                        </Link>
                                      )}
                                    </Menu.Item>
                                  ))}
                                </>
                              )}

                              {navLinks.map((item) => (
                                <Menu.Item key={item.to}>
                                  {({ active }) => (
                                    <Link
                                      to={item.to}
                                      className={`block px-3 py-2 text-[15px] transition ${
                                        active
                                          ? "bg-gray-100 dark:bg-gray-800"
                                          : ""
                                      }`}
                                    >
                                      {t(item.k, { defaultValue: item.def })}
                                    </Link>
                                  )}
                                </Menu.Item>
                              ))}

                              {isEffrEditor && (
                                <Menu.Item>
                                  {({ active }) => (
                                    <Link
                                      to="/club-article"
                                      className={`block px-3 py-2 text-[15px] transition ${
                                        active
                                          ? "bg-gray-100 dark:bg-gray-800"
                                          : ""
                                      }`}
                                    >
                                      {t("common:nav.effrArticles", {
                                        defaultValue: "Articles EFFR",
                                      })}
                                    </Link>
                                  )}
                                </Menu.Item>
                              )}

                              {isAdmin && (
                                <>
                                  <Menu.Item>
                                    {({ active }) => (
                                      <Link
                                        to="/club/create"
                                        className={`block px-3 py-2 text-[15px] transition ${
                                          active
                                            ? "bg-gray-100 dark:bg-gray-800"
                                            : ""
                                        }`}
                                      >
                                        {t("common:nav.createClub", {
                                          defaultValue: "Create club",
                                        })}
                                      </Link>
                                    )}
                                  </Menu.Item>

                                  <Menu.Item>
                                    {({ active }) => (
                                      <Link
                                        to="/admin/app-admins"
                                        className={`block px-3 py-2 text-[15px] transition ${
                                          active
                                            ? "bg-gray-100 dark:bg-gray-800"
                                            : ""
                                        }`}
                                      >
                                        {t("common:nav.manageAdmins", {
                                          defaultValue: "Manage admins",
                                        })}
                                      </Link>
                                    )}
                                  </Menu.Item>

                                  <Menu.Item>
                                    {({ active }) => (
                                      <Link
                                        to="/admin/club-members/add"
                                        className={`block px-3 py-2 text-[15px] transition ${
                                          active
                                            ? "bg-gray-100 dark:bg-gray-800"
                                            : ""
                                        }`}
                                      >
                                        {t("common:nav.addClubMember", {
                                          defaultValue: "Add club member",
                                        })}
                                      </Link>
                                    )}
                                  </Menu.Item>

                                  <Menu.Item>
                                    {({ active }) => (
                                      <Link
                                        to="/createnews"
                                        className={`block px-3 py-2 text-[15px] transition ${
                                          active
                                            ? "bg-gray-100 dark:bg-gray-800"
                                            : ""
                                        }`}
                                      >
                                        {t("common:nav.addNews", {
                                          defaultValue: "Add news",
                                        })}
                                      </Link>
                                    )}
                                  </Menu.Item>

                                  <Menu.Item>
                                    {({ active }) => (
                                      <Link
                                        to="/addpodcast"
                                        className={`block px-3 py-2 text-[15px] transition ${
                                          active
                                            ? "bg-gray-100 dark:bg-gray-800"
                                            : ""
                                        }`}
                                      >
                                        {t("common:nav.addPodcast", {
                                          defaultValue: "Add podcast",
                                        })}
                                      </Link>
                                    )}
                                  </Menu.Item>
                                </>
                              )}

                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    onClick={handleLogout}
                                    className={`w-full text-left block px-3 py-2 text-[15px] transition ${
                                      active
                                        ? "bg-gray-100 dark:bg-gray-800"
                                        : ""
                                    }`}
                                  >
                                    {t("common:nav.logout", {
                                      defaultValue: "Logout",
                                    })}
                                  </button>
                                )}
                              </Menu.Item>
                            </div>
                          </Menu.Items>
                        </Transition>
                      </MenuPortal>
                    )}
                  </>
                )}
              </Menu>
            ) : (
              <div className="flex items-center gap-4">
                <Link
                  to="/login"
                  className="hover:text-orange-500 transition-colors"
                >
                  {t("common:nav.login", { defaultValue: "Login" })}
                </Link>
              </div>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Navbar;